// "Re-run an Insurance month" — preview, run, undo (system admins).
//
// Reversibility-first (CLAUDE.md): the person sees what the row is today and
// what it will be, runs it with an explicit button label, and gets an undo
// that puts back exactly what was there. Server rules (system_admin only,
// months with Bonus Income refused, typed month for approved or paid rows,
// undo refused once the row is decided again) live in
// shared/comp/insurance-month-rerun.ts; this component only draws them.

import { useEffect, useState } from "react";

import { ApiError, apiV11 } from "../api";
import type { InsuranceRerunPreview } from "../api";
import { describeRerun } from "../pages/insurance-rerun-words";
import { Button } from "../ui";
import { Modal } from "./Modal";

type Step =
  | { kind: "pick" }
  | { kind: "loading" }
  | { kind: "preview"; preview: InsuranceRerunPreview }
  | { kind: "running"; preview: InsuranceRerunPreview }
  | { kind: "done"; preview: InsuranceRerunPreview; undoToken: string }
  | { kind: "undone"; period: string }
  | { kind: "error"; message: string };

/** The month the books are being run for: the one that just ended. */
function previousMonth(today: Date): string {
  const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function messageFrom(err: unknown): string {
  if (err instanceof ApiError) {
    const data = err.data as { message?: unknown } | null;
    if (data && typeof data.message === "string") return data.message;
    return err.message;
  }
  return err instanceof Error ? err.message : "Something went wrong. Nothing was changed.";
}

export function InsuranceRerunModal({
  open,
  onClose,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  /** Called after a run or an undo so the list behind the modal reloads. */
  onChanged: () => void;
}) {
  const [period, setPeriod] = useState(() => previousMonth(new Date()));
  const [step, setStep] = useState<Step>({ kind: "pick" });
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (open) {
      setStep({ kind: "pick" });
      setTyped("");
    }
  }, [open]);

  async function loadPreview() {
    setStep({ kind: "loading" });
    try {
      const res = await apiV11.previewInsuranceRerun(period);
      setStep({ kind: "preview", preview: res.preview });
    } catch (err) {
      setStep({ kind: "error", message: messageFrom(err) });
    }
  }

  async function runIt(preview: InsuranceRerunPreview) {
    setStep({ kind: "running", preview });
    try {
      const res = await apiV11.rerunInsuranceMonth(period, preview.needs_typed_confirm ? typed.trim() : undefined);
      setStep({ kind: "done", preview, undoToken: res.undo_token });
      onChanged();
    } catch (err) {
      setStep({ kind: "error", message: messageFrom(err) });
    }
  }

  async function undoIt(undoToken: string) {
    try {
      const res = await apiV11.undoInsuranceRerun(undoToken);
      setStep({ kind: "undone", period: res.period });
      onChanged();
    } catch (err) {
      setStep({ kind: "error", message: messageFrom(err) });
    }
  }

  const busy = step.kind === "loading" || step.kind === "running";
  const previewStep = step.kind === "preview" ? step.preview : null;
  const words = previewStep ? describeRerun(previewStep) : null;
  const typedOk = !previewStep?.needs_typed_confirm || typed.trim() === period;

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Re-run an Insurance month"
      className="modal-narrow"
      data-testid="insurance-rerun-modal"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            Close
          </Button>
          {(step.kind === "pick" || step.kind === "error" || step.kind === "preview") && (
            <Button variant="ghost" type="button" onClick={() => void loadPreview()} data-testid="insurance-rerun-preview">
              {step.kind === "preview" ? "Refresh preview" : "Preview"}
            </Button>
          )}
          {words?.canRun && previewStep && (
            <Button
              variant="primary"
              type="button"
              onClick={() => void runIt(previewStep)}
              disabled={!typedOk}
              data-testid="insurance-rerun-run"
            >
              {words.runLabel}
            </Button>
          )}
        </>
      }
    >
      <div className="stack" style={{ gap: 12 }}>
        <p className="muted" style={{ margin: 0 }}>
          Recalculates one month's Insurance commission pool from the QuickBooks numbers. Preview changes nothing.
          Re-running puts the month back to "calculated", and you can undo it afterwards.
        </p>

        <label htmlFor="insurance-rerun-month" style={{ fontWeight: 600, fontSize: 13 }}>
          Month
        </label>
        <input
          id="insurance-rerun-month"
          type="month"
          value={period}
          onChange={(e) => {
            setPeriod(e.target.value);
            setStep({ kind: "pick" });
            setTyped("");
          }}
          disabled={busy}
          style={{ minHeight: 44, padding: "0 10px", fontSize: 16, maxWidth: 260 }}
        />

        {step.kind === "loading" && <div>Working out what a re-run would do…</div>}
        {step.kind === "running" && <div>Re-running…</div>}

        {words && previewStep && (
          <div className="stack" style={{ gap: 8 }} data-testid="insurance-rerun-words">
            <div>
              <strong>Today:</strong> {words.today ?? "no row for this month yet"}
            </div>
            <div>
              <strong>After the re-run:</strong> {words.after ?? "cannot be calculated"}
            </div>
            {words.warnings.map((w) => (
              <div key={w} style={{ color: "var(--dark-gray)", fontSize: 13 }}>
                {w}
              </div>
            ))}
            {previewStep.needs_typed_confirm && words.canRun && (
              <div className="stack" style={{ gap: 4 }}>
                <label htmlFor="insurance-rerun-typed" style={{ fontWeight: 600, fontSize: 13 }}>
                  Type {period} to confirm
                </label>
                <input
                  id="insurance-rerun-typed"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  style={{ minHeight: 44, padding: "0 10px", fontSize: 16, maxWidth: 260 }}
                />
              </div>
            )}
          </div>
        )}

        {step.kind === "done" && (
          <div className="stack" style={{ gap: 8 }} data-testid="insurance-rerun-done">
            <div>
              <strong>Done.</strong> The month is back to "calculated" and needs approving again.
            </div>
            {step.undoToken ? (
              <Button variant="ghost" type="button" onClick={() => void undoIt(step.undoToken)} data-testid="insurance-rerun-undo">
                Undo this re-run
              </Button>
            ) : (
              <div className="muted">There was no earlier row, so there is nothing to undo back to.</div>
            )}
          </div>
        )}

        {step.kind === "undone" && (
          <div data-testid="insurance-rerun-undone">
            <strong>Undone.</strong> {step.period}'s row is exactly as it was before the re-run.
          </div>
        )}

        {step.kind === "error" && (
          <div role="alert" style={{ color: "var(--danger)" }}>
            {step.message}
          </div>
        )}
      </div>
    </Modal>
  );
}
